import { useSyncExternalStore } from 'react';
import { sm, md, lg } from '@/utils/breakpoints';

/**
 * Which responsive band the viewport is in. Exactly one of the three band
 * flags below is true.
 *
 * - `isMobile`  → below `md` (drawer navigation, phone templates)
 * - `isTablet`  → `md` to `lg`
 * - `isDesktop` → `lg` and up
 *
 * `isPhone` is the one overlapping flag: below `sm` (640px), i.e. phone
 * portrait. It is a strict subset of `isMobile`, for the handful of controls
 * the plan compacts one band earlier than the rest (e.g. the pager).
 *
 * Width in pixels is deliberately not exposed: components branch on the
 * band, and the band is the same contract Tailwind's `md:` / `lg:` prefixes
 * use (both read `src/utils/breakpoints.js`).
 */
export interface Breakpoint {
  /** Below `sm` (640px). Implies `isMobile`; not mutually exclusive with it. */
  isPhone: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export const BREAKPOINT_QUERIES = {
  sm: `(min-width: ${sm}px)`,
  md: `(min-width: ${md}px)`,
  lg: `(min-width: ${lg}px)`,
} as const;

const DESKTOP_DEFAULTS: Breakpoint = {
  isPhone: false,
  isMobile: false,
  isTablet: false,
  isDesktop: true,
};

const hasMatchMedia = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/** Reads the current band synchronously; desktop when `matchMedia` is missing (SSR, jsdom). */
export const readBreakpoint = (): Breakpoint => {
  if (!hasMatchMedia()) return DESKTOP_DEFAULTS;
  const atSm = window.matchMedia(BREAKPOINT_QUERIES.sm).matches;
  const atMd = window.matchMedia(BREAKPOINT_QUERIES.md).matches;
  const atLg = window.matchMedia(BREAKPOINT_QUERIES.lg).matches;
  return {
    isPhone: !atSm,
    isMobile: !atMd,
    isTablet: atMd && !atLg,
    isDesktop: atLg,
  };
};

type LegacyMediaQueryList = MediaQueryList & {
  addListener?: (listener: () => void) => void;
  removeListener?: (listener: () => void) => void;
};

const subscribe = (
  list: LegacyMediaQueryList,
  listener: () => void
): (() => void) => {
  if (typeof list.addEventListener === 'function') {
    list.addEventListener('change', listener);
    return () => list.removeEventListener('change', listener);
  }
  // Safari < 14 only has the deprecated listener API.
  list.addListener?.(listener);
  return () => list.removeListener?.(listener);
};

const same = (a: Breakpoint, b: Breakpoint): boolean =>
  a.isPhone === b.isPhone &&
  a.isMobile === b.isMobile &&
  a.isTablet === b.isTablet &&
  a.isDesktop === b.isDesktop;

/**
 * One store for the whole app: the three `MediaQueryList` objects and their
 * listeners are created when the first consumer subscribes and torn down when
 * the last one unsubscribes, so N consumers share one set of listeners instead
 * of N × 3. The snapshot is a stable object identity, recomputed only when a
 * band actually changes.
 */
const store = (() => {
  let snapshot: Breakpoint = readBreakpoint();
  let unsubscribers: Array<() => void> = [];
  const listeners = new Set<() => void>();

  const update = () => {
    const next = readBreakpoint();
    if (same(snapshot, next)) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe(onStoreChange: () => void): () => void {
      listeners.add(onStoreChange);

      if (listeners.size === 1 && hasMatchMedia()) {
        unsubscribers = Object.values(BREAKPOINT_QUERIES).map((query) =>
          subscribe(window.matchMedia(query) as LegacyMediaQueryList, update)
        );
        // The viewport may have changed between render and subscription.
        update();
      }

      return () => {
        listeners.delete(onStoreChange);
        if (listeners.size === 0) {
          unsubscribers.forEach((unsubscribe) => unsubscribe());
          unsubscribers = [];
        }
      };
    },
    getSnapshot(): Breakpoint {
      // While nobody is subscribed nothing keeps the snapshot fresh (and in
      // tests `window.matchMedia` may have been swapped), so re-read — but
      // keep the same object identity when the band has not moved, as
      // `useSyncExternalStore` requires.
      if (listeners.size === 0) {
        const next = readBreakpoint();
        if (!same(snapshot, next)) snapshot = next;
      }
      return snapshot;
    },
    getServerSnapshot(): Breakpoint {
      return DESKTOP_DEFAULTS;
    },
  };
})();

export function useBreakpoint(): Breakpoint {
  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot
  );
}

export default useBreakpoint;
